/**
 * Field validation and highlighting system for publish validation
 * 
 * This system provides real-time validation for required fields and applies
 * brand-consistent coral-colored borders when validation mode is active.
 * Validation mode is only activated after a publish attempt fails.
 */

/**
 * Validation rules for different field types
 */
export interface FieldValidationRule {
  required: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  customValidator?: (value: any) => boolean;
}

/**
 * Field validation rules mapping
 */
export const FIELD_VALIDATION_RULES: Record<string, FieldValidationRule> = {
  // Project fields
  'project_name': {
    required: true,
    minLength: 3,
    maxLength: 20
  },
  'project_source_name': {
    required: true,
    minLength: 1
  },
  'project_description': {
    required: true,
    minLength: 1
  },
  'project_tags': {
    required: true,
    customValidator: (value: string) => {
      if (!value || value.trim().length === 0) return false;
      const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
      return tags.length > 0;
    }
  },
  'project_portrait_image': {
    required: true,
    customValidator: (value: any) => !!value
  },
  'project_landscape_image': {
    required: true,
    customValidator: (value: any) => !!value && (Array.isArray(value) ? value.length > 0 : true)
  },
  
  // Scene fields
  'scene_name': {
    required: true,
    minLength: 3,
    customValidator: (value: string) => {
      if (!value || value.trim().length < 3) return false;
      // Check if it's not the default "Story X" format
      return !value.match(/^Story\s+\d+$/);
    }
  },
  'scene_description': {
    required: true,
    minLength: 1
  },
  'scene_location': {
    required: true,
    customValidator: (value: any) => {
      // For GPS coordinates validation, value should be an object with lat/lng
      if (!value || typeof value !== 'object') return false;
      if (!value.coordinates) return false;
      const { lat, lng } = value.coordinates;
      if (typeof lat !== 'number' || typeof lng !== 'number') return false;
      if (isNaN(lat) || isNaN(lng)) return false;
      // Basic coordinate range validation
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
      return true;
    }
  },
  'scene_cover_image': {
    required: true,
    customValidator: (value: any) => !!value
  },
  'scene_scan_image': {
    required: true,
    customValidator: (value: any) => !!value
  }
};

/**
 * Validates a field value against its rules
 */
export const validateField = (fieldName: string, value: any): boolean => {
  const rule = FIELD_VALIDATION_RULES[fieldName];
  if (!rule) return true; // No validation rule means field is valid
  
  if (rule.required && (!value || value === '')) {
    return false;
  }
  
  if (value && typeof value === 'string') {
    if (rule.minLength && value.trim().length < rule.minLength) {
      return false;
    }
    if (rule.maxLength && value.trim().length > rule.maxLength) {
      return false;
    }
    if (rule.pattern && !rule.pattern.test(value)) {
      return false;
    }
  }
  
  if (rule.customValidator && !rule.customValidator(value)) {
    return false;
  }
  
  return true;
};

/**
 * Gets brand-consistent error styling for invalid input fields
 * Only applies styling when validation mode is active (after publish attempt)
 */
export const getFieldErrorClass = (isInvalid: boolean, showValidation: boolean = false): string => {
  if (!isInvalid || !showValidation) return '';
  
  return 'border-lureo-danger-ink focus:border-lureo-danger-ink focus-visible:border-lureo-danger-ink';
};

/**
 * Gets brand-consistent error styling for invalid image upload areas
 * Only applies styling when validation mode is active (after publish attempt)
 */
export const getImageErrorClass = (isInvalid: boolean, showValidation: boolean = false): string => {
  if (!isInvalid || !showValidation) return '';
  
  return '!border-lureo-danger-ink';
};
